/**
 * EASY_CONTRACTOR_LandingPage_v01A_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01A
 * Date: 2025-08-31 (America/Denver)
 *
 * Approach:
 * - Wizard/Vertical toggle with progress.
 * - Modular submit pipeline: buildLeadPayload -> toWirePayloadStrings -> postLead.
 * - Sectioned confirmation (readable, no JSON dump).
 * - Minimal fields per request: trade, size, employees/contractors, renewal date (+ basic contact/business/location).
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01A";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/CONTRACTOR-Insurance/";

/* ============================================================
   TYPES
============================================================ */
type Trade =
  | ""
  | "general-contractor"
  | "electrician"
  | "plumber"
  | "hvac"
  | "roofer"
  | "landscaper"
  | "painter"
  | "flooring"
  | "concrete"
  | "masonry"
  | "remodeler"
  | "handyman"
  | "snow-removal"
  | "other";

type BusinessSize = "" | "solo" | "2-5" | "6-10" | "11-25" | "26-50" | "51-100" | "100+";

/** Raw lead payload (typed for app state) */
interface ILeadPayload {
  // Contact & Business
  contactName: string;
  businessName: string;
  email: string;
  phone: string;
  website?: string;

  // Trade & Size
  primaryTrade: Trade;
  otherTrade?: string;
  businessSize: BusinessSize;
  numEmployees?: number;
  numContractors?: number;

  // Location & Renewal
  city?: string;
  state: string;
  zipCode: string;
  renewalDate?: string; // YYYY-MM-DD

  notes?: string;

  // UTM (optional)
  utm?: { source?: string; medium?: string; campaign?: string };

  // Meta
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const digitsOnly = (s: string) => (s ?? "").replace(/\D+/g, "");
const isValidEmail = (email: string) => /.+@.+\..+/.test(email);
const isValidPhone = (raw: string) => digitsOnly(raw).length === 10;
const isValidZipCode = (raw: string) => digitsOnly(raw).length === 5;
const formatPhonePretty = (raw: string) => {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const dash = (v?: string) => (v && v.trim().length ? v : "—");

/* ============================================================
   SLIDES
============================================================ */
interface Slide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption: string;
}
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_Contractors_Team_Preview.jpg",
    alt: "Crew of contractors on a job site.",
    title: "Coverage for Contractors",
    subline: "GL, tools, jobsites—tell us your trade and size.",
    smallCaption: "Fast quotes. Clear options.",
  },
  {
    src: imagePath + "AdobeStock_Roofing_Team_Preview.jpg",
    alt: "Roofers working on a residential home.",
    title: "Built for Busy Schedules",
    subline: "Quick intake—so you can get back to work.",
    smallCaption: "No-obligation, friendly advice.",
  },
  {
    src: imagePath + "AdobeStock_Electrician_Preview.jpg",
    alt: "Electrician checking panel.",
    title: "Right-Sized Policies",
    subline: "Solo, small crew, or growing team—we’ll match carriers.",
    smallCaption: "Licensed advisors. Multiple carriers.",
  },
];

/* ============================================================
   MAIN
============================================================ */
export const EASY_CONTRACTOR_LandingPage_v02O_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact & Business
  const [contactName, setContactName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");

  // Trade & Size
  const [primaryTrade, setPrimaryTrade] = useState<Trade>("");
  const [otherTrade, setOtherTrade] = useState("");
  const [businessSize, setBusinessSize] = useState<BusinessSize>("");
  const [numEmployees, setNumEmployees] = useState<string>(""); // keep string to allow blank
  const [numContractors, setNumContractors] = useState<string>("");

  // Location & Renewal
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [renewalDate, setRenewalDate] = useState<string>("");

  // Misc
  const [notes, setNotes] = useState("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Business", "Trade & Size", "Location & Renewal", "Confirmation"] as const;

  /* ---------- Touched + validation (blur-driven) ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  // Validators
  const V = {
    contactName: contactName.trim().length >= 2,
    businessName: businessName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    primaryTrade: primaryTrade !== "",
    otherTrade: primaryTrade !== "other" || (primaryTrade === "other" && otherTrade.trim().length >= 2),
    businessSize: businessSize !== "",

    numEmployees: numEmployees.trim() === "" || (/^\d{1,4}$/.test(numEmployees) && Number(numEmployees) >= 0),
    numContractors: numContractors.trim() === "" || (/^\d{1,4}$/.test(numContractors) && Number(numContractors) >= 0),

    state: state !== "",
    zip: isValidZipCode(zipCode),
    city: city.trim().length === 0 || city.trim().length >= 2,

    website: website.trim().length === 0 || /^https?:\/\//i.test(website),

    renewalDate: renewalDate.trim().length === 0 || /^\d{4}-\d{2}-\d{2}$/.test(renewalDate),
    notes: notes.trim().length === 0 || notes.trim().length >= 2,
  };

  const valid = useMemo(() => {
    return (
      V.contactName &&
      V.businessName &&
      V.email &&
      V.phone &&
      V.primaryTrade &&
      V.otherTrade &&
      V.businessSize &&
      V.numEmployees &&
      V.numContractors &&
      V.state &&
      V.zip &&
      V.city &&
      V.website &&
      V.renewalDate &&
      V.notes
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.contactName && V.businessName && V.email && V.phone && V.website;
      case 1:
        return V.primaryTrade && V.otherTrade && V.businessSize && V.numEmployees && V.numContractors;
      case 2:
        return V.state && V.zip && V.city && V.renewalDate;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["contactName", "businessName", "email", "phone", "website"]);
    else if (currentStep === 1) touchMany(["primaryTrade", "otherTrade", "businessSize", "numEmployees", "numContractors"]);
    else if (currentStep === 2) touchMany(["state", "zip", "city", "renewalDate"]);

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ============================================================
     PAYLOAD & SUBMIT (MODULARIZED)
  ============================================================ */
  function buildLeadPayload(): ILeadPayload {
    const numEmp = numEmployees.trim() === "" ? undefined : Number(numEmployees);
    const num1099 = numContractors.trim() === "" ? undefined : Number(numContractors);

    return {
      contactName: contactName.trim(),
      businessName: businessName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),
      website: website.trim() || "",

      primaryTrade,
      otherTrade: otherTrade.trim() || "",
      businessSize,
      numEmployees: Number.isFinite(numEmp) ? numEmp : undefined,
      numContractors: Number.isFinite(num1099) ? num1099 : undefined,

      city: city.trim() || "",
      state: String(state),
      zipCode: String(zipCode),
      renewalDate: renewalDate || "",

      notes: notes.trim() || "",

      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.contractor.quote",
      itemClass: "quote.request",
      itemType: "legacy.contractor.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (typeof v === "number") out[k] = Number.isFinite(v) ? `${v}` : "";
      else if (typeof v === "boolean") out[k] = v ? "true" : "false";
      else if (v && typeof v === "object") {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else out[k] = "";
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch { resJson = null; }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // In wizard mode, ignore submit on steps 0–1 and just advance
    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Touch all required so feedback appears if invalid
    setTouched((t) => ({
      ...t,
      contactName: true, businessName: true, email: true, phone: true, website: true,
      primaryTrade: true, otherTrade: true, businessSize: true, numEmployees: true, numContractors: true,
      state: true, zip: true, city: true, renewalDate: true,
      notes: true,
    }));

    if (!valid) {
      setSubmitError("Please complete the required fields.");
      return;
    }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 600));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmittedApp(appPayload);
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Clamp active slide index
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Easy <span className="text-primary">Contractor</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
              <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with background Carousel */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Fast Quotes</Badge>
                  <Badge bg="light" text="dark">Multiple Carriers</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" onClick={scrollToForm}>Get Started</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Quick Contractor Quote</div>
                      <small className="text-body-secondary">Basic info to match you with the right policy.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM START */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {/* STEP 1: CONTACT & BUSINESS */}
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Contact &amp; Business</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="contactName">Your Name</Form.Label>
                                    <Form.Control
                                      id="contactName"
                                      value={contactName}
                                      onChange={(e) => setContactName(e.target.value)}
                                      placeholder="Jane Contractor"
                                      required
                                      {...vp("contactName", V.contactName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your name.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="businessName">Business Name</Form.Label>
                                    <Form.Control
                                      id="businessName"
                                      value={businessName}
                                      onChange={(e) => setBusinessName(e.target.value)}
                                      placeholder="Acme Builders LLC"
                                      required
                                      {...vp("businessName", V.businessName)}
                                    />
                                    <Form.Control.Feedback type="invalid">Please enter your business name.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="email">Email</Form.Label>
                                    <Form.Control
                                      id="email"
                                      type="email"
                                      value={email}
                                      onChange={(e) => setEmail(e.target.value)}
                                      placeholder="you@example.com"
                                      required
                                      {...vp("email", V.email)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="phone">Phone</Form.Label>
                                    <Form.Control
                                      id="phone"
                                      inputMode="tel"
                                      value={phone}
                                      onChange={(e) => setPhone(e.target.value)}
                                      placeholder="(555) 123-4567"
                                      required
                                      {...vp("phone", V.phone)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="website">Website (optional)</Form.Label>
                                    <Form.Control
                                      id="website"
                                      value={website}
                                      onChange={(e) => setWebsite(e.target.value)}
                                      placeholder="https://your-site.com"
                                      {...vpOpt("website", !!website.trim(), V.website)}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 2: TRADE & SIZE */}
                          {currentStep === 1 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Trade &amp; Size</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="primaryTrade">Primary Trade</Form.Label>
                                    <Form.Select
                                      id="primaryTrade"
                                      value={primaryTrade}
                                      onChange={(e) => setPrimaryTrade(e.target.value as Trade)}
                                      required
                                      {...vp("primaryTrade", V.primaryTrade)}
                                    >
                                      <option value="">Select trade</option>
                                      <option value="general-contractor">General Contractor</option>
                                      <option value="electrician">Electrician</option>
                                      <option value="plumber">Plumber</option>
                                      <option value="hvac">HVAC</option>
                                      <option value="roofer">Roofer</option>
                                      <option value="landscaper">Landscaper</option>
                                      <option value="painter">Painter</option>
                                      <option value="flooring">Flooring</option>
                                      <option value="concrete">Concrete</option>
                                      <option value="masonry">Masonry</option>
                                      <option value="remodeler">Remodeler</option>
                                      <option value="handyman">Handyman</option>
                                      <option value="snow-removal">Snow Removal</option>
                                      <option value="other">Other</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="otherTrade">If Other (optional)</Form.Label>
                                    <Form.Control
                                      id="otherTrade"
                                      value={otherTrade}
                                      onChange={(e) => setOtherTrade(e.target.value)}
                                      placeholder="Describe your trade"
                                      {...vp("otherTrade", V.otherTrade)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      Please describe your trade (min 2 chars).
                                    </Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="businessSize">Business Size</Form.Label>
                                    <Form.Select
                                      id="businessSize"
                                      value={businessSize}
                                      onChange={(e) => setBusinessSize(e.target.value as BusinessSize)}
                                      required
                                      {...vp("businessSize", V.businessSize)}
                                    >
                                      <option value="">Select size</option>
                                      <option value="solo">Solo</option>
                                      <option value="2-5">2–5</option>
                                      <option value="6-10">6–10</option>
                                      <option value="11-25">11–25</option>
                                      <option value="26-50">26–50</option>
                                      <option value="51-100">51–100</option>
                                      <option value="100+">100+</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="numEmployees"># Employees (W-2)</Form.Label>
                                    <Form.Control
                                      id="numEmployees"
                                      inputMode="numeric"
                                      value={numEmployees}
                                      onChange={(e) => setNumEmployees(e.target.value.replace(/[^\d]/g, ""))}
                                      placeholder="e.g., 4"
                                      {...vpOpt("numEmployees", !!numEmployees.trim(), V.numEmployees)}
                                    />
                                  </Col>

                                  <Col md={3}>
                                    <Form.Label htmlFor="numContractors"># Contractors (1099)</Form.Label>
                                    <Form.Control
                                      id="numContractors"
                                      inputMode="numeric"
                                      value={numContractors}
                                      onChange={(e) => setNumContractors(e.target.value.replace(/[^\d]/g, ""))}
                                      placeholder="e.g., 2"
                                      {...vpOpt("numContractors", !!numContractors.trim(), V.numContractors)}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 3: LOCATION & RENEWAL */}
                          {currentStep === 2 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Location &amp; Renewal</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={4}>
                                    <Form.Label htmlFor="city">City (optional)</Form.Label>
                                    <Form.Control
                                      id="city"
                                      value={city}
                                      onChange={(e) => setCity(e.target.value)}
                                      placeholder="City"
                                      {...vp("city", V.city)}
                                    />
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="state">State</Form.Label>
                                    <Form.Select
                                      id="state"
                                      value={state}
                                      onChange={(e) => setState(e.target.value)}
                                      required
                                      {...vp("state", V.state)}
                                    >
                                      <option value="">Select</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="zipCode">Zip</Form.Label>
                                    <Form.Control
                                      id="zipCode"
                                      inputMode="numeric"
                                      value={zipCode}
                                      onChange={(e) => setZipCode(e.target.value)}
                                      placeholder="#####"
                                      required
                                      {...vp("zip", V.zip)}
                                    />
                                    <Form.Control.Feedback type="invalid">Enter a 5-digit zip.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={6}>
                                    <Form.Label htmlFor="renewalDate">Current Policy Renewal (optional)</Form.Label>
                                    <Form.Control
                                      id="renewalDate"
                                      type="date"
                                      value={renewalDate}
                                      onChange={(e) => setRenewalDate(e.target.value)}
                                      {...vpOpt("renewalDate", !!renewalDate, V.renewalDate)}
                                    />
                                    <div className="form-text">Approximate is okay.</div>
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="notes">Notes (optional)</Form.Label>
                                    <Form.Control
                                      id="notes"
                                      as="textarea"
                                      rows={3}
                                      value={notes}
                                      onChange={(e) => setNotes(e.target.value)}
                                      placeholder="Any special jobs, tools/equipment, or requirements?"
                                      {...vp("notes", V.notes)}
                                    />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 4: CONFIRMATION */}
                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls (no buttons on final step) */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" /> Submitting...
                                  </>
                                ) : (
                                  <>Get My Quote</>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      /* ---------- VERTICAL (all-in-one) ---------- */
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              {/* Contact & Business */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Contact &amp; Business</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="contactNameV">Your Name</Form.Label>
                                      <Form.Control
                                        id="contactNameV"
                                        value={contactName}
                                        onChange={(e) => setContactName(e.target.value)}
                                        placeholder="Jane Contractor"
                                        required
                                        {...vp("contactName", V.contactName)}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="businessNameV">Business Name</Form.Label>
                                      <Form.Control
                                        id="businessNameV"
                                        value={businessName}
                                        onChange={(e) => setBusinessName(e.target.value)}
                                        placeholder="Acme Builders LLC"
                                        required
                                        {...vp("businessName", V.businessName)}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="emailV">Email</Form.Label>
                                      <Form.Control
                                        id="emailV"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        {...vp("email", V.email)}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="phoneV">Phone</Form.Label>
                                      <Form.Control
                                        id="phoneV"
                                        inputMode="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="(555) 123-4567"
                                        required
                                        {...vp("phone", V.phone)}
                                      />
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="websiteV">Website (optional)</Form.Label>
                                      <Form.Control
                                        id="websiteV"
                                        value={website}
                                        onChange={(e) => setWebsite(e.target.value)}
                                        placeholder="https://your-site.com"
                                        {...vpOpt("website", !!website.trim(), V.website)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Trade & Size */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Trade &amp; Size</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="primaryTradeV">Primary Trade</Form.Label>
                                      <Form.Select
                                        id="primaryTradeV"
                                        value={primaryTrade}
                                        onChange={(e) => setPrimaryTrade(e.target.value as Trade)}
                                        required
                                        {...vp("primaryTrade", V.primaryTrade)}
                                      >
                                        <option value="">Select trade</option>
                                        <option value="general-contractor">General Contractor</option>
                                        <option value="electrician">Electrician</option>
                                        <option value="plumber">Plumber</option>
                                        <option value="hvac">HVAC</option>
                                        <option value="roofer">Roofer</option>
                                        <option value="landscaper">Landscaper</option>
                                        <option value="painter">Painter</option>
                                        <option value="flooring">Flooring</option>
                                        <option value="concrete">Concrete</option>
                                        <option value="masonry">Masonry</option>
                                        <option value="remodeler">Remodeler</option>
                                        <option value="handyman">Handyman</option>
                                        <option value="snow-removal">Snow Removal</option>
                                        <option value="other">Other</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="otherTradeV">If Other (optional)</Form.Label>
                                      <Form.Control
                                        id="otherTradeV"
                                        value={otherTrade}
                                        onChange={(e) => setOtherTrade(e.target.value)}
                                        placeholder="Describe your trade"
                                        {...vp("otherTrade", V.otherTrade)}
                                      />
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="businessSizeV">Business Size</Form.Label>
                                      <Form.Select
                                        id="businessSizeV"
                                        value={businessSize}
                                        onChange={(e) => setBusinessSize(e.target.value as BusinessSize)}
                                        required
                                        {...vp("businessSize", V.businessSize)}
                                      >
                                        <option value="">Select size</option>
                                        <option value="solo">Solo</option>
                                        <option value="2-5">2–5</option>
                                        <option value="6-10">6–10</option>
                                        <option value="11-25">11–25</option>
                                        <option value="26-50">26–50</option>
                                        <option value="51-100">51–100</option>
                                        <option value="100+">100+</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={3}>
                                      <Form.Label htmlFor="numEmployeesV"># Employees (W-2)</Form.Label>
                                      <Form.Control
                                        id="numEmployeesV"
                                        inputMode="numeric"
                                        value={numEmployees}
                                        onChange={(e) => setNumEmployees(e.target.value.replace(/[^\d]/g, ""))}
                                        placeholder="e.g., 4"
                                        {...vpOpt("numEmployees", !!numEmployees.trim(), V.numEmployees)}
                                      />
                                    </Col>

                                    <Col md={3}>
                                      <Form.Label htmlFor="numContractorsV"># Contractors (1099)</Form.Label>
                                      <Form.Control
                                        id="numContractorsV"
                                        inputMode="numeric"
                                        value={numContractors}
                                        onChange={(e) => setNumContractors(e.target.value.replace(/[^\d]/g, ""))}
                                        placeholder="e.g., 2"
                                        {...vpOpt("numContractors", !!numContractors.trim(), V.numContractors)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Location & Renewal */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Location &amp; Renewal</strong>
                                  </legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={4}>
                                      <Form.Label htmlFor="cityV">City (optional)</Form.Label>
                                      <Form.Control
                                        id="cityV"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        placeholder="City"
                                        {...vp("city", V.city)}
                                      />
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="stateV">State</Form.Label>
                                      <Form.Select
                                        id="stateV"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                        {...vp("state", V.state)}
                                      >
                                        <option value="">Select</option>
                                        {US_STATES.map((s) => (
                                          <option key={s.code} value={s.code}>{s.name}</option>
                                        ))}
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="zipCodeV">Zip</Form.Label>
                                      <Form.Control
                                        id="zipCodeV"
                                        inputMode="numeric"
                                        value={zipCode}
                                        onChange={(e) => setZipCode(e.target.value)}
                                        placeholder="#####"
                                        required
                                        {...vp("zip", V.zip)}
                                      />
                                      <Form.Control.Feedback type="invalid">Enter a 5-digit zip.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={6}>
                                      <Form.Label htmlFor="renewalDateV">Current Policy Renewal (optional)</Form.Label>
                                      <Form.Control
                                        id="renewalDateV"
                                        type="date"
                                        value={renewalDate}
                                        onChange={(e) => setRenewalDate(e.target.value)}
                                        {...vpOpt("renewalDate", !!renewalDate, V.renewalDate)}
                                      />
                                      <div className="form-text">Approximate is okay.</div>
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="notesV">Notes (optional)</Form.Label>
                                      <Form.Control
                                        id="notesV"
                                        as="textarea"
                                        rows={3}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Any special jobs, tools/equipment, or requirements?"
                                        {...vp("notes", V.notes)}
                                      />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </Row>

                            {/* Submit */}
                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                  {/* FORM END */}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three quick steps.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us your trade & size</h6>
                  <p className="mb-0 text-body-secondary">Solo or crew—employees, contractors, renewal date.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">We compare carriers</h6>
                  <p className="mb-0 text-body-secondary">We’ll match policies for your jobs and tools.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">Review & bind</h6>
                  <p className="mb-0 text-body-secondary">Clear options, friendly advisors, fast binding.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Easy Contractor Insurance. All rights reserved.
              <div className="mt-2">
                Insurance services subject to licensing and state availability. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033375434">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ------------------------------------------------------------
   Confirmation block (Sectioned, readable, no JSON)
------------------------------------------------------------ */
const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {/* Status */}
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A licensed representative will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        {/* Sections */}
        <div className="mt-3">
          <Section title="Contact & Business">
            <KV label="Contact" value={dash(app.contactName)} />
            <KV label="Business" value={dash(app.businessName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={formatPhonePretty(app.phone)} />
            <KV label="Website" value={dash(app.website)} />
          </Section>

          <Section title="Trade & Size">
            <KV label="Primary Trade" value={app.primaryTrade ? app.primaryTrade.replace(/-/g, " ") : "—"} />
            <KV label="Other Trade" value={dash(app.otherTrade)} />
            <KV label="Business Size" value={dash(app.businessSize)} />
            <KV label="# Employees (W-2)" value={typeof app.numEmployees === "number" ? String(app.numEmployees) : "—"} />
            <KV label="# Contractors (1099)" value={typeof app.numContractors === "number" ? String(app.numContractors) : "—"} />
          </Section>

          <Section title="Location & Renewal">
            <KV label="City" value={dash(app.city)} />
            <KV label="State / Zip" value={`${dash(app.state)} ${dash(app.zipCode)}`} />
            <KV label="Renewal Date" value={dash(app.renewalDate)} />
          </Section>

          <Section title="Notes">
            <div className="p-3 bg-body-tertiary border rounded">{dash(app.notes)}</div>
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033375434" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Back to top
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">
      {children}
    </Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

export default EASY_CONTRACTOR_LandingPage_v02O_WIZARD;
